import styled from 'styled-components';

export const WizardCard = styled.div`
  display: flex;
  flex-direction: column;
  background: ${(props) => props.theme.colors.background || '#FFFFFF'};
  border-radius: 12px;
  border: 2px solid ${(props) => props.theme.colors.border || '#E5E7EB'};
  overflow: hidden;
  max-width: 100%;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
`;

export const WizardHeader = styled.div`
  padding: 16px 20px;
  background: ${(props) => props.theme.colors.backgroundSecondary || '#F9FAFB'};
  border-bottom: 1px solid ${(props) => props.theme.colors.border || '#E5E7EB'};
`;

export const ProgressBar = styled.div`
  width: 100%;
  height: 8px;
  background: ${(props) => props.theme.colors.border || '#E5E7EB'};
  border-radius: 4px;
  overflow: hidden;
  margin-bottom: 8px;
`;

export const ProgressBarFill = styled.div`
  height: 100%;
  width: ${(props) => props.$percentage}%;
  background: ${(props) => props.theme.colors.primary || '#4F46E5'};
  transition: width 0.3s ease;
  border-radius: 4px;
`;

export const ProgressText = styled.div`
  font-size: 13px;
  font-weight: 600;
  color: ${(props) => props.theme.colors.textSecondary || '#6B7280'};
  text-align: center;
`;

export const QuestionContainer = styled.div`
  padding: 24px;
  flex: 1;
`;

export const QuestionNumber = styled.div`
  font-size: 12px;
  font-weight: 700;
  color: ${(props) => props.theme.colors.primary || '#4F46E5'};
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 8px;
`;

export const QuestionText = styled.div`
  font-size: 16px;
  font-weight: 600;
  color: ${(props) => props.theme.colors.text || '#1F2937'};
  line-height: 1.5;
  margin-bottom: 20px;
`;

export const OptionsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

export const OptionItem = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 14px 16px;
  background: ${(props) => props.$isSelected 
    ? props.theme.colors.primaryLight || '#EEF2FF'
    : props.theme.colors.backgroundSecondary || '#F9FAFB'};
  border: 2px solid ${(props) => props.$isSelected 
    ? props.theme.colors.primary || '#4F46E5'
    : 'transparent'};
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s ease;
  user-select: none;

  &:hover {
    background: ${(props) => props.$isSelected 
      ? props.theme.colors.primaryLight || '#EEF2FF'
      : '#F3F4F6'};
    border-color: ${(props) => props.$isSelected 
      ? props.theme.colors.primary || '#4F46E5'
      : '#D1D5DB'};
  }

  &:active {
    transform: scale(0.98);
  }
`;

export const OptionCheckbox = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  min-width: 20px;
  border: 2px solid ${(props) => props.$isSelected 
    ? props.theme.colors.primary || '#4F46E5'
    : '#D1D5DB'};
  border-radius: 4px;
  background: ${(props) => props.$isSelected 
    ? props.theme.colors.primary || '#4F46E5'
    : '#FFFFFF'};
  transition: all 0.2s ease;
`;

export const OptionLabel = styled.span`
  font-size: 14px;
  color: ${(props) => props.theme.colors.text || '#374151'};
  line-height: 1.5;
  flex: 1;
`;

export const CustomInputContainer = styled.div`
  margin-top: 8px;
  width: 100%;
`;

export const CustomInput = styled.input`
  width: 100%;
  padding: 12px 14px;
  border: 2px solid #D1D5DB;
  border-radius: 8px;
  font-size: 14px;
  font-family: inherit;
  outline: none;
  transition: border-color 0.2s ease;

  &::placeholder {
    color: #9CA3AF;
  }

  &:focus {
    border-color: ${(props) => props.theme.colors.primary || '#4F46E5'};
  }

  &:hover {
    border-color: #9CA3AF;
  }
`;

export const NavigationContainer = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 12px;
  padding: 20px 24px;
  background: ${(props) => props.theme.colors.backgroundSecondary || '#F9FAFB'};
  border-top: 1px solid ${(props) => props.theme.colors.border || '#E5E7EB'};
`;

export const NavigationButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 12px 24px;
  font-size: 14px;
  font-weight: 600;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s ease;
  min-width: 120px;

  ${(props) => props.$variant === 'primary' && `
    background: ${props.theme.colors.primary || '#4F46E5'};
    color: #FFFFFF;
    
    &:hover:not(:disabled) {
      background: ${props.theme.colors.primaryDark || '#4338CA'};
      transform: translateY(-1px);
      box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
    }
  `}

  ${(props) => props.$variant === 'secondary' && `
    background: transparent;
    color: ${props.theme.colors.text || '#374151'};
    border: 2px solid ${props.theme.colors.border || '#E5E7EB'};
    
    &:hover:not(:disabled) {
      background: ${props.theme.colors.backgroundSecondary || '#F9FAFB'};
      border-color: ${props.theme.colors.text || '#374151'};
    }
  `}

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
    transform: none;
  }

  &:active:not(:disabled) {
    transform: translateY(0);
  }
`;

export const ReviewContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

export const ReviewQuestionItem = styled.div`
  padding: 16px;
  background: ${(props) => props.$isReadOnly 
    ? props.theme.colors.backgroundSecondary || '#F9FAFB'
    : '#FFFFFF'};
  border: 2px solid ${(props) => props.$isAnswered 
    ? props.theme.colors.primary || '#4F46E5'
    : props.theme.colors.border || '#E5E7EB'};
  border-radius: 8px;
  opacity: ${(props) => props.$isReadOnly ? 0.8 : 1};
`;

export const ReviewQuestionTitle = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: ${(props) => props.theme.colors.text || '#1F2937'};
  margin-bottom: 12px;
  flex: 1;
`;

export const ReviewAnswersList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

export const ReviewAnswer = styled.div`
  font-size: 13px;
  color: ${(props) => props.$isEmpty 
    ? props.theme.colors.textSecondary || '#9CA3AF'
    : props.theme.colors.text || '#374151'};
  font-style: ${(props) => props.$isEmpty ? 'italic' : 'normal'};
  line-height: 1.5;
`;

export const ReviewEditButton = styled.button`
  padding: 6px 12px;
  font-size: 12px;
  font-weight: 600;
  color: ${(props) => props.theme.colors.primary || '#4F46E5'};
  background: transparent;
  border: 1px solid ${(props) => props.theme.colors.primary || '#4F46E5'};
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: ${(props) => props.theme.colors.primaryLight || '#EEF2FF'};
  }

  &:active {
    transform: scale(0.95);
  }
`;

export const SubmittedBadge = styled.span`
  padding: 4px 12px;
  font-size: 11px;
  font-weight: 700;
  color: #10B981;
  background: #D1FAE5;
  border-radius: 12px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

